import React, { useState, useMemo } from "react";
import dayjs from "dayjs";
import {
    Box,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    OutlinedInput,
    Checkbox,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    CircularProgress,
    Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../../theme";

import {
    useGetTopicBreakoutsQuery,
    useGetTopicDailyTrendQuery,
} from "../../../../features/market_pulse/marketPulseApiSlice";
import { skipToken } from "@reduxjs/toolkit/query/react";

/* ───── reusable ─────────────────────────────────────────────────────── */
const makeSearchFilter = (fields, term) =>
    term
        ? (row) =>
            fields.some((f) =>
                String(row[f] ?? "").toLowerCase().includes(term.toLowerCase())
            )
        : () => true;

/* ===================================================================== */
/*  COMPONENT                                                            */
/* ===================================================================== */
const TopicTab = ({
    type,                    // "b2b" | "b2c"
    selectedDate,
    /* search props come from <MarketPulse /> ---------------------------- */
    search,
    searchFields,
    setSearch,
    setSearchFields,
    /* 7-day timeline range / setter (lifted state) ---------------------- */
    startDay,
    setStartDay,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    /* ───────────────── TIMEFRAME (local to this tab) ────────────────── */
    const [timeframe, setTimeframe] = useState("day");     // day | week | month



    /* ── ensure we have an initial start-day on first mount ──────────── */
    React.useEffect(() => {
        if (!startDay) {
            setStartDay(selectedDate.clone().subtract(6, "day").startOf("day"));
        }
    }, [startDay, selectedDate, setStartDay]);

    /* always keep a derived 7-day window: [start, start6] -------------- */
    const timelineRange = React.useMemo(
        () => (startDay ? [startDay, startDay.clone().add(6, "day")] : null),
        [startDay]
    );

    /* ─── 1.  BREAKOUT COUNTS  ────────────────────────────────────────── */
    const {
        data: breakoutData = { rows: [] },
        isLoading: breakoutLoading,
        isFetching: breakoutFetching,
    } = useGetTopicBreakoutsQuery(
        { type, range: timeframe, date: selectedDate.format("YYYY-MM-DD") },
        { refetchOnMountOrArgChange: true }
    );
    const breakoutRows = breakoutData.rows;

    /* ─── 2.  FILTERED ROWS (search) ──────────────────────────────────── */
    const filterFn = useMemo(
        () => makeSearchFilter(searchFields, search),
        [search, searchFields]
    );
    const filteredRows = useMemo(
        () => breakoutRows.filter(filterFn),
        [breakoutRows, filterFn]
    );

    /* ─── 3.  HOOKS FOR TIMELINE ACCORDION ────────────────────────────── */
    const [timelineOpen, setTimelineOpen] = useState(false);

    /* pull daily trend only when accordion is open ---------------------- */
    const topicCsv = filteredRows
        .slice(0, 100) // cap to keep URL short
        .map((r) => `'${r.TOPIC_ID.replace(/'/g, "''")}'`)
        .join(",");

    const { data: trend = { rows: [] }, isFetching: trendLoading } =
        useGetTopicDailyTrendQuery(
            timelineOpen && timelineRange
                ? {
                    start: timelineRange[0].format("YYYY-MM-DD"),
                    end: timelineRange[1].format("YYYY-MM-DD"),
                    topicIds: topicCsv,
                }
                : skipToken
        );

    /* build dynamic 7-day columns & rows -------------------------------- */
    const dayColDefs = useMemo(() => {
        /* show just the ID / Topic columns until startDay is ready */
        if (!startDay) {
            return [
                { field: "TOPIC_ID", headerName: "Topic ID", minWidth: 130 },
                { field: "TOPIC", headerName: "Topic", minWidth: 180, flex: 1 },
            ];
        }

        const days = [];
        for (let d = startDay.clone(); d.diff(startDay, "day") <= 6; d = d.add(1, "day"))
            days.push(d.clone());

        return [
            { field: "TOPIC_ID", headerName: "Topic ID", minWidth: 130 },
            { field: "TOPIC", headerName: "Topic", minWidth: 180, flex: 1 },
            ...days.map((d) => ({
                field: d.format("YYYY-MM-DD"),
                headerName: d.format("MMM D"),
                type: "number",
                minWidth: 110,
                valueFormatter: ({ value }) =>
                    value !== undefined ? value.toLocaleString() : "",
            })),
        ];
    }, [startDay]);

    /* reshape trend → topic-by-day rows --------------------------------- */
    const timelineRows = useMemo(() => {
        if (!timelineOpen) return [];

        const map = {};
        trend.rows.forEach(({ DAY, TOPIC_ID, TOPIC, COUNT }) => {
            if (!map[TOPIC_ID]) map[TOPIC_ID] = { TOPIC_ID, TOPIC };
            map[TOPIC_ID][DAY] = COUNT;
        });
        return Object.values(map);
    }, [trend, timelineOpen]);

    /* columns for breakout table ---------------------------------------- */
    const countsCols = useMemo(
        () => [
            { field: "TOPIC_ID", headerName: "Topic ID", flex: 1.2 },
            { field: "TOPIC", headerName: "Topic", flex: 1.6 },
            { field: "DESCRIPTION", headerName: "Description", flex: 3 },
            {
                field: "RECORD_COUNT",
                headerName: `Count (${timeframe})`,
                type: "number",
                flex: 0.8,
                valueFormatter: ({ value }) => value.toLocaleString(),
            },
        ],
        [timeframe]
    );

    /* ─── 4.  UI  ──────────────────────────────────────────────────────── */
    return (
        <Box>

            {/* ── TOOL-BAR (search) ───────────────────────────────────────── */}
            <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
                <TextField
                    label="Search"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />

                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id={`search-field-${type}`}>Search Fields</InputLabel>
                    <Select
                        multiple
                        labelId={`search-field-${type}`}
                        value={searchFields}
                        onChange={(e) => setSearchFields(e.target.value)}
                        input={
                            <OutlinedInput
                                label="Search Fields"
                                sx={{ "& .MuiOutlinedInput-notchedOutline": { borderWidth: 2 } }}
                            />
                        }
                        renderValue={(sel) => sel.join(", ")}
                    >
                        {searchFields.map((v) => (
                            <MenuItem key={v} value={v}>
                                <Checkbox checked={searchFields.includes(v)} />
                                <ListItemText primary={v} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {/* timeframe selector  (ONLY for this tab) */}
                <FormControl size="small">
                    <Select value={timeframe} onChange={e => setTimeframe(e.target.value)}>
                        <MenuItem value="day">Day</MenuItem>
                        <MenuItem value="week">Week</MenuItem>
                        <MenuItem value="month">Month</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* ── ACCORDION 1  :  counts table ────────────────────────────── */}
            <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Topic Counts ({timeframe})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {(breakoutLoading || breakoutFetching) ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                            <CircularProgress size={48} sx={{ color: colors.greenAccent[400] }} />
                        </Box>
                    ) : (
                        <DataGrid
                            autoHeight
                            rows={filteredRows}
                            columns={countsCols}
                            pageSize={25}
                            getRowId={(r) => r.TOPIC_ID}
                            disableRowSelectionOnClick
                            sx={{
                                "& .MuiDataGrid-columnHeaders": { fontSize: "1rem", fontWeight: 600 },
                            }}
                        />
                    )}
                </AccordionDetails>
            </Accordion>

            {/* ── ACCORDION 2  :  7-day timeline ──────────────────────────── */}
            <Accordion
                defaultExpanded={false}
                onChange={(_, open) => setTimelineOpen(open)}
                sx={{
                    mt: 2,
                    background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[800]} 100%)`,
                    borderRadius: 2,
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" color="white">
                        7-Day Topic Timeline
                    </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ backgroundColor: colors.primary[700] }}>
                    {/* date-picker row  ------------------------------------------------ */}
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
                        {startDay && (
                            <Typography sx={{ color: colors.primary[100], fontWeight: 600 }}>
                                {startDay.format("MMM D")} – {startDay.clone().add(6, "day").format("MMM D")}
                            </Typography>
                        )}

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Start Day"
                                disableFuture
                                maxDate={selectedDate}
                                minDate={selectedDate.clone().subtract(180, "day")}
                                value={startDay}
                                onChange={(d) => d && setStartDay(d.startOf("day"))}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: {
                                            maxWidth: 140,
                                            "& input": { color: colors.primary[100] },
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: colors.primary[100],
                                            },
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Box>


                    {trendLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                            <CircularProgress size={48} />
                        </Box>
                    ) : (
                        <DataGrid
                            autoHeight
                            rows={timelineRows}
                            columns={dayColDefs}
                            pageSize={25}
                            getRowId={(r) => r.TOPIC_ID}
                            disableRowSelectionOnClick
                            sx={{
                                "& .MuiDataGrid-columnHeaders": { fontSize: "1rem", fontWeight: 600 },
                            }}
                        />
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default TopicTab;
